import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';

import { parseUnitOptionValue } from './format';
import {
  orderReplacementDetailSchema,
  orderReplacementListSchema,
  replacementEligibleItemsSchema,
} from './schemas';
import type {
  OrderReplacementDetail,
  OrderReplacementList,
  ReplacementEligibleItem,
  RequestReplacementValues,
} from './types';

/**
 * Every replacement request raised against one order, plus the backend's replacement-window
 * verdict. Drives routing, the listing rows and the Request Replacement pill from a single fetch
 * (ADR-0022). The array is unpaginated and order-scoped, so most-recent-first is a client-side
 * sort — ISO-8601 strings compare lexicographically, so no date parsing is needed.
 */
export async function listOrderReplacementRequests(orderId: string): Promise<OrderReplacementList> {
  const path = endpoints.orders.replacementRequests(orderId);
  const parsed = parseContract(orderReplacementListSchema, await apiClient.get(path), path);
  parsed.requests.sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
  return parsed;
}

/** One replacement request's read-only detail. Unlike the listing this needs no client-side
 *  massaging — the Activity Timeline is derived at render time in `timeline.ts`. */
export async function getOrderReplacementRequest(
  orderId: string,
  requestId: string,
): Promise<OrderReplacementDetail> {
  const path = endpoints.orders.replacementRequestDetail(orderId, requestId);
  return parseContract(orderReplacementDetailSchema, await apiClient.get(path), path);
}

/** Every line item of the order with at least one unit still eligible for replacement, each
 *  carrying its quantity and its gap-preserving `eligibleUnits` (ADR-0022). */
export async function listReplacementEligibleItems(
  orderId: string,
): Promise<ReplacementEligibleItem[]> {
  const path = endpoints.orders.replacementEligibleItems(orderId);
  return parseContract(replacementEligibleItemsSchema, await apiClient.get(path), path);
}

/** Raises one replacement request against one Unit. The composite option value is split back into
 *  `orderItemId` + `unitIndex` here, and only image asset ids go on the wire (never storage keys
 *  round-tripped back). An empty description is omitted rather than sent blank. */
export async function createOrderReplacementRequest(
  orderId: string,
  values: RequestReplacementValues,
): Promise<void> {
  const { orderItemId, unitIndex } = parseUnitOptionValue(values.unit);
  const description = values.description.trim();
  return apiClient.post(endpoints.orders.replacementRequests(orderId), {
    orderItemId,
    unitIndex,
    reason: values.reason,
    ...(description ? { description } : {}),
    assetIds: values.images.map((f) => f.id),
  });
}
